import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  GitHub, 
  Slack, 
  Calendar, 
  GitBranch, 
  FileText, 
  Database,
  Activity,
  Clock,
  Users,
  Settings,
  Bell,
  X,
  Menu,
  ChevronLeft,
  Link2
} from 'react-feather';
import api from '../services/api.ts';
import ActivityFeed from '../components/ActivityFeed.js';
import IntegrationCard from '../components/IntegrationCard.js';
import DemoSection from '../components/DemoSection.js';
import { format } from 'date-fns';
import { CasprLogo } from '../components/CasprLogo.js';
import DocumentsView from '../components/DocumentsView.js';
import { DiscordLogo } from '../components/icons/DiscordLogo.js';
import { GoogleLogo } from '../components/icons/GoogleLogo.js';
// import { QueryClientProvider } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from 'react-query';
import TeamProgress from '../components/TeamProgress';
import IndividualContributions from '../components/IndividualContributions';
const queryClient = new QueryClient();
interface IntegrationStatus {
  github: boolean;
  slack: boolean;
  jira: boolean;
  linear: boolean;
  notion: boolean;
  hubspot: boolean;
  confluence: boolean;
  discord: boolean;
  google: boolean;
  lastSync?: string;
}

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const [integrationStatus, setIntegrationStatus] = React.useState<IntegrationStatus>({
    github: false,
    slack: false,
    jira: false,
    linear: false,
    notion: false,
    hubspot: false,
    confluence: false,
    discord: false,
    google: false,
    lastSync: undefined
  });
  const [activeTab, setActiveTab] = React.useState('activity');
  const location = useLocation();
  const [isIntegrationsPanelOpen, setIsIntegrationsPanelOpen] = useState(false);

  const fetchIntegrations = async () => {
    try {
      const response = await api.get('/integrations/status');
      console.log('Integration status response:', response.data);
      if (response.data.status === 'success') {
        const integrations = response.data.integrations;
        // If we have any active integrations but no lastSync, use current time
        if (Object.values(integrations).some(Boolean) && !integrations.lastSync) {
          integrations.lastSync = new Date().toISOString();
        }
        setIntegrationStatus(integrations);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  React.useEffect(() => {
    // Initial fetch
    fetchIntegrations();
    
    // Check URL parameters
    const status = searchParams.get('status');
    const integration = searchParams.get('integration');
    
    if (status === 'success' && integration) {
      // Wait a moment before checking status after successful connection
      setTimeout(() => {
        console.log('Rechecking integration status after successful connection');
        fetchIntegrations();
      }, 1000);
    }
  }, [searchParams]);

  React.useEffect(() => {
    const integration = searchParams.get('integration');
    const status = searchParams.get('status');
    const error = searchParams.get('error');
    
    if (integration && status === 'success') {
      toast.success(`Successfully connected to ${integration}!`);
    }
    
    if (error) {
      toast.error(error);
    }
  }, [searchParams]);

  const handleConnect = async (authUrl: string) => {
    try {
      const response = await api.get(authUrl);
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Error starting auth flow:', error);
      toast.error('Failed to start authentication process');
    }
  };

  const getActiveIntegrationsCount = (status: IntegrationStatus): number => {
    return Object.entries(status)
      .filter(([key]) => key !== 'lastSync')
      .filter(([_, value]) => value === true)
      .length;
  };

  const stats = [
    { 
      name: 'Active Integrations', 
      value: getActiveIntegrationsCount(integrationStatus).toString(), 
      icon: Activity, 
      color: 'text-blue-500' 
    }
  ];

  const integrations = [
    {
      id: 'github',
      name: 'GitHub',
      description: 'Connect to GitHub repositories',
      icon: GitHub,
      authUrl: '/api/github/auth',
      isConnected: integrationStatus.github,
      bgColor: 'bg-gray-800',
      hoverColor: 'hover:bg-gray-900'
    },
    {
      id: 'google',
      name: 'Google Workspace',
      description: 'Connect to Google Docs, Sheets, and Drive',
      icon: GoogleLogo,
      authUrl: '/api/google/auth',
      isConnected: integrationStatus.google,
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Connect to Slack workspace',
      icon: Slack,
      authUrl: '/api/slack/auth',
      isConnected: integrationStatus.slack,
      bgColor: 'bg-[#4A154B]',
      hoverColor: 'hover:bg-[#3a1139]'
    },
    {
      id: 'jira',
      name: 'Jira',
      description: 'Track issues, sprints, and project updates',
      icon: Calendar,
      authUrl: '/api/jira/auth',
      isConnected: integrationStatus.jira,
      bgColor: 'bg-[#0052CC]',
      hoverColor: 'hover:bg-[#0065ff]'
    },
    {
      id: 'linear',
      name: 'Linear',
      description: 'Streamline your project management workflow',
      icon: GitBranch,
      authUrl: '/api/linear/auth',
      isConnected: integrationStatus.linear,
      bgColor: 'bg-[#5E6AD2]',
      hoverColor: 'hover:bg-[#4752C4]'
    },
    {
      id: 'notion',
      name: 'Notion',
      description: 'Sync essential docs and updates',
      icon: FileText,
      authUrl: '/api/notion/auth',
      isConnected: integrationStatus.notion,
      bgColor: 'bg-[#1A73E8]',
      hoverColor: 'hover:bg-[#1565C0]'
    },
    // {
    //   id: 'hubspot',
    //   name: 'Hubspot',
    //   description: 'Integrate with Hubspot for CRM',
    //   icon: Database,
    //   authUrl: '/api/hubspot/auth',
    //   isConnected: integrationStatus.hubspot,
    //   bgColor: 'bg-[#FF7A59]',
    //   hoverColor: 'hover:bg-[#FF5733]'
    // },
    {
      id: 'confluence',
      name: 'Confluence',
      description: 'Connect to Confluence for documentation',
      icon: FileText,
      authUrl: '/api/confluence/auth',
      isConnected: integrationStatus.confluence,
      bgColor: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700'
    },
    {
      id: 'discord',
      name: 'Discord',
      description: 'Connect your Discord server to track messages and activities',
      icon: DiscordLogo,
      authUrl: '/api/discord/auth',
      isConnected: integrationStatus.discord,
      bgColor: 'bg-[#5865F2]',
      hoverColor: 'hover:bg-[#4752C4]'
    }
  ];

  const API_BASE_URL = 'https://api.usepeppr.ai';

  const checkNotionConnection = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notion/status`, {
        credentials: 'include'  // Important! This sends the session cookie
      });
      const data = await response.json();
      console.log('Notion status check response:', data);  // Add this for debugging
      return data.connected;
    } catch (error) {
      console.error('Error checking Notion connection:', error);
      return false;
    }
  };

  const LastSyncWidget = () => {
    const hasActiveIntegrations = Object.values(integrationStatus)
      .filter(val => typeof val === 'boolean')
      .some(Boolean);

    const formatLastSync = (lastSync?: string, hasActive = false) => {
      if (!hasActive) return 'No active integrations';
      if (!lastSync) return 'Never';
      
      const date = new Date(lastSync);
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes === 1) return '1m ago';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours === 1) return '1h ago';
      if (diffHours < 24) return `${diffHours}h ago`;
      
      return format(date, 'MMM d, h:mm a');
    };

    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-green-50 rounded-xl">
            <Clock className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <p className="text-gray-600 text-sm">Last Sync</p>
            <p className="text-3xl font-semibold text-gray-900 mt-1">
              {formatLastSync(integrationStatus.lastSync, hasActiveIntegrations)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Documents', href: '/documents' },
    { name: 'Team Progress', href: '/team-progress' },
    { name: 'Employee Contributions', href: '/individual-contributions' },
  ];

  // Update the tab options
  const handleTabClick = (tab: string) => {
    if (tab === 'integrations') {
      setIsIntegrationsPanelOpen(!isIntegrationsPanelOpen);
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <CasprLogo />
            </div>
            {/* <div className="flex items-center ml-8 space-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium ${
                    location.pathname === item.href
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  } py-4`}
                >
                  {item.name}
                </Link>
              ))}
            </div> */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setIsIntegrationsPanelOpen(true)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Database className="h-4 w-4" />
                <span className="text-sm font-medium">Integrations</span>
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-500">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-500">
                <Settings className="h-5 w-5" />
              </button>
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Users className="h-4 w-4 text-gray-500" />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => handleTabClick('activity')}
              className={`${
                activeTab === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Recent Activity
            </button>
            <button
              onClick={() => handleTabClick('documents')}
              className={`${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Documents
            </button>
            <button
              onClick={() => handleTabClick('team-progress')}
              className={`${
                activeTab === 'team-progress'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Team Progress
            </button>
            <button
              onClick={() => handleTabClick('individual-contributions')}
              className={`${
                activeTab === 'individual-contributions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Individual Contributions
            </button>
          </nav>
        </div>
      </div>

      {/* Integrations Side Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-96 bg-gray-50 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isIntegrationsPanelOpen ? 'translate-x-0' : 'translate-x-full'
        } z-50`}
      >
        <div className="h-full flex flex-col">
          {/* Panel Header */}
          <div className="px-4 py-6 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Integrations</h2>
              <button
                onClick={() => setIsIntegrationsPanelOpen(false)}
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Overview Cards */}
            <div className="space-y-4 mb-6">
              {/* Active Integrations Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <Activity className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Active</p>
                    <p className="text-3xl font-semibold text-gray-900 mt-1">
                      {getActiveIntegrationsCount(integrationStatus)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Sync Card */}
              <LastSyncWidget />
            </div>

            {/* Integrations List */}
            <div className="space-y-3">
              {integrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  {...integration}
                  onStatusChange={fetchIntegrations}
                  compact={true}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'documents' && (
          <QueryClientProvider client={queryClient}>
            <DocumentsView />
          </QueryClientProvider>
        )}

        {activeTab === 'activity' && (
          <div className="bg-[#111827] rounded-lg shadow-sm p-6">
            <ActivityFeed />
          </div>
        )}

        {activeTab === 'team-progress' && (
          <TeamProgress />
        )}

        {activeTab === 'individual-contributions' && (
          <IndividualContributions />
        )}
      </div>

      {/* Overlay with reduced opacity */}
      {isIntegrationsPanelOpen && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-40"
          onClick={() => setIsIntegrationsPanelOpen(false)}
        />
      )}
    </div>
  );
} 